import _defineProperty from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _toConsumableArray from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/toConsumableArray.js";
import _slicedToArray from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _objectWithoutProperties from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["state", "value", "checked", "defaultChecked"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useRef, useEffect } from 'react';
import { useControlledState, useTagName, useEvent, useWrapElement, useForkRef } from 'ariakit-utils/hooks';
import { createHook, createComponent, createElement } from 'ariakit-utils/system';
import { useCommand } from '../command/command.js';
import { C as CheckboxCheckedContext } from '../__utils-fdd6f8a4.js';
import { jsx } from 'react/jsx-runtime';

function getStateChecked(stateValue, elementValue) {
  if (stateValue === undefined) return;

  if (elementValue) {
    if (Array.isArray(stateValue)) {
      return stateValue.includes(elementValue);
    }

    return stateValue === elementValue;
  }

  if (Array.isArray(stateValue)) {
    return false;
  }

  if (typeof stateValue === "boolean") {
    return stateValue;
  }

  return false;
}

function setMixed(element, mixed) {
  if (mixed) {
    element.indeterminate = true;
  } else if (element.indeterminate) {
    element.indeterminate = false;
  }
}

function isNativeCheckbox(tagName, type) {
  return tagName === "input" && (!type || type === "checkbox");
}
/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component. If the element is not a native checkbox, the hook will
 * return additional props to make sure it's accessible.
 * @see https://ariakit.org/components/checkbox
 * @example
 * ```jsx
 * const props = useCheckbox({ as: "div" });
 * <Role {...props}>Accessible checkbox</Role>
 * ```
 */


var useCheckbox = createHook(function (_ref) {
  var state = _ref.state,
      value = _ref.value,
      checkedProp = _ref.checked,
      defaultChecked = _ref.defaultChecked,
      props = _objectWithoutProperties(_ref, _excluded);

  var _useControlledState = useControlledState(defaultChecked != null ? defaultChecked : false, checkedProp != null ? checkedProp : getStateChecked(state == null ? void 0 : state.value, value)),
      _useControlledState2 = _slicedToArray(_useControlledState, 2),
      checked = _useControlledState2[0],
      setChecked = _useControlledState2[1];

  var ref = useRef(null);
  var tagName = useTagName(ref, props.as || "input");
  var nativeCheckbox = isNativeCheckbox(tagName, props.type);
  var mixed = checked ? checked === "mixed" : undefined;
  var isChecked = checked === "mixed" ? false : checked;
  useEffect(function () {
    var element = ref.current;
    if (!element) return;
    setMixed(element, mixed);
    element.checked = isChecked;
  }, [mixed, isChecked]);
  var onChangeProp = props.onChange;
  var onChange = useEvent(function (event) {
    if (props.disabled) {
      event.stopPropagation();
      event.preventDefault();
      return;
    }

    setMixed(event.currentTarget, mixed);

    if (!nativeCheckbox) {
      // If the element is not a native checkbox, we need to manually update
      // its checked property.
      event.currentTarget.checked = !event.currentTarget.checked;
    }

    onChangeProp == null ? void 0 : onChangeProp(event);
    if (event.defaultPrevented) return;
    var elementChecked = event.currentTarget.checked;
    setChecked(elementChecked);
    state == null ? void 0 : state.setValue(function (prevValue) {
      if (!value) return elementChecked;
      if (!Array.isArray(prevValue)) return value;
      if (elementChecked) return [].concat(_toConsumableArray(prevValue), [value]);
      return prevValue.filter(function (v) {
        return v !== value;
      });
    });
  });
  var onClickProp = props.onClick;
  var onClick = useEvent(function (event) {
    onClickProp == null ? void 0 : onClickProp(event);
    if (event.defaultPrevented) return;
    if (nativeCheckbox) return; // @ts-expect-error The onChange event expects a ChangeEvent, but here we
    // need to pass a MouseEvent.

    onChange(event);
  });
  props = useWrapElement(props, function (element) {
    return /*#__PURE__*/jsx(CheckboxCheckedContext.Provider, {
      value: isChecked,
      children: element
    });
  }, [isChecked]);
  props = _objectSpread(_objectSpread({
    role: !nativeCheckbox ? "checkbox" : undefined,
    type: nativeCheckbox ? "checkbox" : undefined,
    "aria-checked": checked
  }, props), {}, {
    ref: useForkRef(ref, props.ref),
    onChange: onChange,
    onClick: onClick
  });
  props = useCommand(_objectSpread({
    clickOnEnter: !nativeCheckbox
  }, props));
  return _objectSpread({
    value: nativeCheckbox ? value : undefined,
    checked: isChecked
  }, props);
});
/**
 * A component that renders a native accessible checkbox. If another element is
 * passed to the `as` prop, this component will make sure the rendered element
 * is accessible.
 * @see https://ariakit.org/components/checkbox
 * @example
 * ```jsx
 * <Checkbox as="div">Accessible checkbox</Checkbox>
 * ```
 */

var Checkbox = createComponent(function (props) {
  var htmlProps = useCheckbox(props);
  return createElement("input", htmlProps);
});
export { Checkbox, useCheckbox };